import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaChartLine, FaUsers, FaNewspaper, FaClock, FaTrophy, 
  FaArrowUp, FaArrowDown, FaMinus, FaDownload, FaCalendarAlt,
  FaGraduationCap, FaCheckCircle, FaExclamationTriangle, FaFilter
} from 'react-icons/fa';
import api from '../utils/api';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month'); // 'week', 'month', 'trimester', 'year'
  const [selectedClass, setSelectedClass] = useState('all');
  const [data, setData] = useState({
    overview: {},
    students: [],
    articles: {},
    workshops: {},
    classes: [],
    trends: {}
  });

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, selectedClass]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, studentsRes, articlesRes, classesRes] = await Promise.all([
        api.get(`/analytics/overview?range=${dateRange}&class=${selectedClass}`),
        api.get(`/analytics/students?range=${dateRange}&class=${selectedClass}`),
        api.get(`/analytics/articles?range=${dateRange}&class=${selectedClass}`),
        api.get('/articles/classes')
      ]);
      
      setData({
        overview: overviewRes.data,
        students: studentsRes.data.students || [],
        articles: articlesRes.data,
        classes: classesRes.data.classes || [],
        workshops: overviewRes.data.workshops || {},
        trends: overviewRes.data.trends || {}
      });
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <FaArrowUp className="text-green-500" />;
    if (trend < 0) return <FaArrowDown className="text-red-500" />;
    return <FaMinus className="text-gray-400" />;
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue' }}>
            TABLEAU DE BORD ANALYTIQUE
          </h1>
          <p className="text-gray-600">Vue d'ensemble des performances de l'école</p>
        </div>
        
        <div className="flex gap-4">
          {/* Date Range Filter */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="trimester">Ce trimestre</option>
            <option value="year">Cette année</option>
          </select>
          
          {/* Class Filter */}
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value="all">Toutes les classes</option>
            {data.classes.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          
          {/* Export Button */}
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700">
            <FaDownload /> Exporter
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          icon={<FaUsers />}
          title="Élèves actifs"
          value={data.overview.activeStudents || 0}
          trend={data.trends.students || 0}
          subtitle="sur la période"
          color="blue"
        />
        <KPICard
          icon={<FaNewspaper />}
          title="Articles soumis"
          value={data.articles.submitted || 0}
          trend={data.trends.articles || 0}
          subtitle={`${data.articles.validated || 0} validés`}
          color="green"
        />
        <KPICard
          icon={<FaClock />}
          title="Taux de ponctualité"
          value={`${data.overview.punctualityRate || 0}%`}
          trend={data.trends.punctuality || 0}
          subtitle="rendus à l'heure"
          color="yellow"
        />
        <KPICard
          icon={<FaTrophy />}
          title="Score moyen"
          value={data.overview.averageScore || 0}
          trend={data.trends.score || 0}
          subtitle="tous ateliers confondus"
          color="purple"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Performance Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaChartLine className="text-blue-500" /> Évolution des performances
          </h2>
          <PerformanceChart data={data.trends.history || []} />
        </div>
        
        {/* Top Students */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaTrophy className="text-yellow-500" /> Top 5 Élèves
          </h2>
          <TopStudentsList students={data.students.slice(0, 5)} />
        </div>
      </div>

      {/* Workshops & Articles Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Workshops Stats */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaGraduationCap className="text-purple-500" /> Performance par atelier
          </h2>
          <WorkshopsStats workshops={data.workshops} />
        </div>
        
        {/* Articles Pipeline */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaNewspaper className="text-green-500" /> Pipeline d'articles
          </h2>
          <ArticlesPipeline articles={data.articles} />
        </div>
      </div>

      {/* Alerts & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Students Needing Attention */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaExclamationTriangle className="text-orange-500" /> Élèves à surveiller
          </h2>
          <StudentsNeedingAttention students={data.students.filter(s => (s.score || 0) < 50)} />
        </div>
        
        {/* Upcoming Deadlines */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaCalendarAlt className="text-red-500" /> Deadlines à venir
          </h2>
          <UpcomingDeadlines deadlines={data.articles.upcomingDeadlines || []} />
        </div>
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ icon, title, value, trend, subtitle, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl p-6 border-2 ${colors[color]}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
          {trend > 0 && <FaArrowUp />}
          {trend < 0 && <FaArrowDown />}
          {trend !== 0 && <span>{Math.abs(trend)}%</span>}
        </div>
      </div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-xs text-gray-400 mt-1">{subtitle}</div>
    </motion.div>
  );
};

// Performance Chart (simplified)
const PerformanceChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400">
        <div className="text-center">
          <FaChartLine className="text-4xl mx-auto mb-2" />
          <p>Pas assez de données pour afficher le graphique</p>
        </div>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 100);
  
  return (
    <div className="h-64 flex items-end gap-2">
      {data.map((point, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center">
          <div 
            className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
            style={{ height: `${(point.value / maxValue) * 200}px` }}
            title={`${point.label}: ${point.value}`}
          />
          <span className="text-xs text-gray-500 mt-2 truncate w-full text-center">{point.label}</span>
        </div>
      ))}
    </div>
  );
};

// Top Students List
const TopStudentsList = ({ students }) => {
  if (!students || students.length === 0) {
    return <p className="text-gray-500 text-center py-8">Aucun élève à afficher</p>;
  }

  return (
    <div className="space-y-3">
      {students.map((student, idx) => (
        <div key={student.id || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
            idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-400' : 'bg-gray-300'
          }`}>
            {idx + 1}
          </div>
          <div className="flex-1">
            <div className="font-medium text-gray-900">{student.name || student.firstName}</div>
            <div className="text-xs text-gray-500">{student.cohort || student.class}</div>
          </div>
          <div className="text-lg font-bold text-blue-600">{student.score || 0}</div>
        </div>
      ))}
    </div>
  );
};

// Workshops Stats
const WorkshopsStats = ({ workshops }) => {
  const workshopsList = Object.entries(workshops || {}).map(([name, data]) => ({
    name,
    ...data
  }));

  if (workshopsList.length === 0) {
    return <p className="text-gray-500 text-center py-8">Aucune donnée d'atelier</p>;
  }

  return (
    <div className="space-y-4">
      {workshopsList.map((workshop, idx) => (
        <div key={idx}>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium">{workshop.name}</span>
            <span className="text-gray-600">{workshop.avgScore || 0}/100</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${
                (workshop.avgScore || 0) >= 70 ? 'bg-green-500' :
                (workshop.avgScore || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${workshop.avgScore || 0}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {workshop.attempts || 0} tentatives • {workshop.participants || 0} participants
          </div>
        </div>
      ))}
    </div>
  );
};

// Articles Pipeline
const ArticlesPipeline = ({ articles }) => {
  const stages = [
    { key: 'assigned', label: 'Commandés', color: 'bg-gray-400' },
    { key: 'submitted', label: 'Soumis', color: 'bg-yellow-500' },
    { key: 'underReview', label: 'En correction', color: 'bg-orange-500' },
    { key: 'validated', label: 'Validés', color: 'bg-green-500' },
    { key: 'published', label: 'Publiés', color: 'bg-blue-600' }
  ];

  const total = stages.reduce((sum, s) => sum + (articles[s.key] || 0), 0) || 1;

  return (
    <div className="space-y-4">
      {stages.map((stage, idx) => {
        const count = articles[stage.key] || 0;
        const percent = Math.round((count / total) * 100);
        
        return (
          <div key={stage.key}>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium">{stage.label}</span>
              <span className="text-gray-600">{count}</span>
            </div>
            <div className="h-6 bg-gray-100 rounded-lg overflow-hidden relative">
              <div 
                className={`h-full ${stage.color} transition-all`}
                style={{ width: `${percent}%` }}
              />
              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
                {percent > 5 && `${percent}%`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Students Needing Attention
const StudentsNeedingAttention = ({ students }) => {
  if (!students || students.length === 0) {
    return (
      <div className="text-center py-8 text-green-600">
        <FaCheckCircle className="text-4xl mx-auto mb-2" />
        <p>Tous les élèves sont sur la bonne voie !</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {students.slice(0, 5).map((student, idx) => (
        <div key={student.id || idx} className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <FaExclamationTriangle className="text-orange-500" />
          <div className="flex-1">
            <div className="font-medium text-gray-900">{student.name || student.firstName}</div>
            <div className="text-xs text-gray-500">Score: {student.score || 0}/100</div>
          </div>
          <button className="text-sm text-orange-600 hover:text-orange-800">
            Voir profil
          </button>
        </div>
      ))}
    </div>
  );
};

// Upcoming Deadlines
const UpcomingDeadlines = ({ deadlines }) => {
  if (!deadlines || deadlines.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <FaCalendarAlt className="text-4xl mx-auto mb-2" />
        <p>Aucune deadline proche</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {deadlines.slice(0, 5).map((deadline, idx) => {
        const daysLeft = Math.ceil((new Date(deadline.date) - new Date()) / (1000 * 60 * 60 * 24));
        const isUrgent = daysLeft <= 1;
        
        return (
          <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${isUrgent ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${isUrgent ? 'bg-red-500 text-white' : 'bg-gray-200'}`}>
              {daysLeft}j
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">{deadline.title}</div>
              <div className="text-xs text-gray-500">{deadline.class} • {deadline.pending} en attente</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnalyticsDashboard;
